import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import { addDays, isValidPrepTime, PREP_LATEST_TIME, wallTimeInZone } from '@kraftwerk/shared';
import { db } from '../db/local.js';
import { today } from '../db/nutrition.js';
import { acknowledgePrep, prepCheckFor, prepSummaryFor } from '../db/prep.js';
import { readProfile, updatePrepReminderTime } from '../db/profile.js';
import { currentHouseholdId } from '../db/shopping.js';

const DEFAULT_TIME = '20:00';

/**
 * Ab wann „Für morgen" auf Heute steht. Morgens interessiert der nächste Tag
 * nicht, und eine Kachel, die man ständig wegscrollt, liest man auch abends nicht.
 */
const SHOW_FROM: string = '15:00';

/**
 * „Für morgen" auf dem Heute-Bildschirm.
 *
 * Zeigt live, was die Abend-Erinnerung sagen würde — dieselbe Rechnung wie im
 * Scheduler. Wer die Erinnerung nicht einschaltet, hat hier trotzdem alles;
 * wer sie einschaltet, kann sie hier quittieren.
 */
export function PrepTile({ highlight }: { highlight: boolean }): React.JSX.Element {
  const { t } = useTranslation();
  const ref = useRef<HTMLElement>(null);
  const [tomorrow, setTomorrow] = useState<string | null>(null);
  const [afternoon, setAfternoon] = useState(false);

  useEffect(() => {
    void readProfile(db).then((profile) => {
      setTomorrow(addDays(today(profile.timezone), 1));
      setAfternoon(wallTimeInZone(new Date(), profile.timezone) >= SHOW_FROM);
    });
  }, []);

  const householdId = useLiveQuery(() => currentHouseholdId(db), [], null);
  const summary = useLiveQuery(
    () => (tomorrow ? prepSummaryFor(tomorrow, householdId ?? null, db) : undefined),
    [tomorrow, householdId],
  );
  const check = useLiveQuery(() => (tomorrow ? prepCheckFor(tomorrow, db) : undefined), [tomorrow]);

  // Erst scrollen, wenn die Kachel ihren Inhalt hat: Beim ersten Zeichnen steht
  // dort nur „Lädt …", danach wächst sie nach unten aus dem Bild (am iPhone
  // gesehen, Runde 2). Ein Bild abwarten, damit auch Kacheln darüber stehen.
  const loaded = summary !== undefined;
  useEffect(() => {
    if (!highlight || !loaded) return;
    const frame = requestAnimationFrame(() => {
      ref.current?.scrollIntoView({ block: 'center' });
    });
    return () => cancelAnimationFrame(frame);
  }, [highlight, loaded]);

  const nothing =
    summary &&
    summary.tasks.length === 0 &&
    summary.missing.length === 0 &&
    summary.unknownIngredients.length === 0;

  // Sichtbar am Nachmittag, beim Antippen der Benachrichtigung und solange es
  // etwas zu quittieren gibt — sonst nicht.
  const pendingCheck = check !== undefined && check !== null && !check.acknowledgedAt;
  if (!afternoon && !highlight && !pendingCheck) return <></>;

  return (
    <section
      ref={ref}
      aria-labelledby="prep-heading"
      className={`kw-tile flex scroll-mb-28 flex-col gap-3 ${highlight ? 'border-accent' : ''}`}
    >
      <h2 id="prep-heading" className="kw-kicker">
        {t('prep.heading')}
      </h2>

      {!summary ? (
        <p className="text-sm text-ink-muted">{t('session.loading')}</p>
      ) : nothing ? (
        <p className="text-sm text-ink-muted">{t('prep.nothing')}</p>
      ) : (
        <>
          {summary.tasks.length > 0 ? (
            <ul className="flex flex-col gap-1">
              {summary.tasks.map((task) => (
                <li key={task.recipeId} className="text-sm">
                  <span className="font-medium">{task.note}</span>{' '}
                  <span className="text-ink-muted">· {task.recipeName}</span>
                </li>
              ))}
            </ul>
          ) : null}

          {summary.missing.length > 0 ? (
            <div>
              <h3 className="kw-label text-warn">{t('prep.missing')}</h3>
              <ul className="mt-1 flex flex-col gap-1">
                {summary.missing.map((line) => (
                  <li key={line.key} className="text-sm">
                    {line.name}{' '}
                    <span className="text-ink-muted">
                      ·{' '}
                      {line.toBuyG !== null && line.toBuyG > 0
                        ? t('prep.missingGrams', { grams: line.toBuyG })
                        : t('prep.missingCheck')}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {summary.unknownIngredients.length > 0 ? (
            <p className="text-xs text-ink-muted">
              {t('prep.unknownIngredients', { recipes: summary.unknownIngredients.join(', ') })}
            </p>
          ) : null}
        </>
      )}

      {check && !check.acknowledgedAt ? (
        <button type="button" className="kw-btn" onClick={() => void acknowledgePrep(check.id, db)}>
          {t('prep.done')}
        </button>
      ) : null}
      {check?.acknowledgedAt ? (
        <p role="status" className="text-sm text-good">
          {t('prep.doneAlready')}
        </p>
      ) : null}
    </section>
  );
}

/**
 * Abend-Erinnerung ein- und ausschalten — in den Einstellungen. Aus ist die
 * Vorgabe: Benachrichtigungen schaltet man selbst ein, sie kommen nicht ungefragt.
 */
export function PrepReminderSetting(): React.JSX.Element {
  const { t } = useTranslation();
  const timeId = useId();
  const current = useLiveQuery(async () => (await readProfile(db)).prepReminderTime, [], null);
  const [draft, setDraft] = useState<string>(DEFAULT_TIME);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (current) setDraft(current);
  }, [current]);

  const save = (value: string | null) => {
    setError(null);
    if (value !== null && !isValidPrepTime(value)) {
      setError(t('prep.tooLate', { time: PREP_LATEST_TIME }));
      return;
    }
    void updatePrepReminderTime(value, db).then((ok) => {
      if (!ok) setError(t('nutrition.targets.noProfile'));
    });
  };

  return (
    <div className="flex flex-col gap-2">
      <p className="text-xs text-ink-muted">
        {current ? t('prep.reminderOn', { time: current }) : t('prep.reminderOff')}
      </p>
      <div className="flex flex-wrap items-end gap-2">
        <label htmlFor={timeId} className="flex w-32 flex-col gap-1 text-xs text-ink-muted">
          {t('prep.time')}
          <input
            id={timeId}
            type="time"
            value={draft}
            max={PREP_LATEST_TIME}
            onChange={(event) => setDraft(event.target.value)}
            className="kw-field text-ink"
          />
        </label>
        <button type="button" className="kw-btn kw-btn-small" onClick={() => save(draft)}>
          {current ? t('prep.change') : t('prep.enable')}
        </button>
        {current ? (
          <button
            type="button"
            className="kw-btn kw-btn-quiet kw-btn-small"
            onClick={() => save(null)}
          >
            {t('prep.disable')}
          </button>
        ) : null}
      </div>
      {error ? (
        <p role="alert" className="text-xs text-danger">
          {error}
        </p>
      ) : null}
    </div>
  );
}
